import { Col, Form } from "react-bootstrap";
import { RegField, RegFieldName } from "../../models/application_info";
import { $t, t_key } from "../../plugins/i18n";
import AdsAutocomplete from "./AdsAutocomplete";
import DatePicker from 'react-datepicker';
import Select from "react-select";
import React, { useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

type SelectOption = {
  value: string;
  label: string;
};

export type FormFieldProps = {
  fld: RegField,
  formName?: string,
  value?: any,
  errorMessage?: any,
  disabled: boolean,
  readOnly: boolean,
  change?(e: any): void,
  isMobile: boolean,
} & typeof defaultProps;

const defaultProps = {
  readOnly: false,
  disabled: false,
  isMobile: false,
}

const AriaDesc = {
  ErrorPrefix: "error_",
  MobilePhone: "mobile-phone-desc"
}

export const FormField = (props: FormFieldProps) => {
  const { i18n } = useTranslation();
  const { control, formState: {errors} } = useFormContext();
  const [allOptions, setAllOptions] = React.useState<SelectOption[] | null>(null);

  const getLocalizedLabel = (labels: {[key: string]: string} | null | undefined): string | null => {
    if (!labels) {
      return null;
    }

    const activeLocale = i18n.language.toLowerCase();
    const baseLocale = activeLocale.split("-")[0];
    const localized = labels[activeLocale] ?? labels[baseLocale];
    if (localized?.trim()) {
      return localized;
    }

    return null;
  };

  const resolveFieldLabel = (field: RegField): string => {
    return getLocalizedLabel(field.labels) ?? field.name;
  };

  const resolveChoiceLabel = (value: string, labels?: {[key: string]: string} | null): string => {
    return getLocalizedLabel(labels) ?? value;
  };

  const mapValueToSelectOptions = (value: any, isMulti: boolean): SelectOption[] | SelectOption | null => {
    const availableOptions = allOptions ?? [];
    const getValue = (item: any): string | null => {
      if (!item) return null;
      if (typeof item === "string") return item;
      if (typeof item.value === "string") return item.value;
      return null;
    };

    if (isMulti) {
      const valueList = Array.isArray(value) ? value : [];
      return valueList
        .map(getValue)
        .filter((itemValue): itemValue is string => !!itemValue)
        .map((itemValue) => availableOptions.find((opt) => opt.value === itemValue))
        .filter((option): option is SelectOption => !!option);
    }

    const singleValue = getValue(value);
    if (!singleValue) {
      return null;
    }

    return availableOptions.find((option) => option.value === singleValue) ?? null;
  };

  const toBoolean = (value: unknown): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value === 1;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      return normalized === "true" || normalized === "1";
    }
    return false;
  };

  /* note(kristen):
   * Aria description needs to refer to an element which
   * has corresponding id and has the text inside of the element.
   *
   * Correct example:
   *```
   * <div id="desc-id">This is a description</div>
   * <div aria-describedby="desc-id">
   *    this element is described by element 'desc-id'
   * </div>
   * ```
   */
  const ariaDescription = (fieldName: string): string => {
    const errorsExist: boolean = errors.field_values !== undefined

    if (!errorsExist) {
      if (fieldName === RegFieldName.MobilePhone) {
        return AriaDesc.MobilePhone
      }
    }

    // if input is invalid add field description and field error
    // messages together
    if (errorsExist) {
      var description = `${AriaDesc.ErrorPrefix}${fieldName}`
      if (fieldName === RegFieldName.MobilePhone) {
        return `${description} ${AriaDesc.MobilePhone}`
      }

      return description
    }

    return "";
  }

  const renderFieldControl = (
    serviceField: RegField,
  ): JSX.Element => {
    if (serviceField.type === "date") {
      return (
        <>
          <Controller
            control={control}
            name={`field_values.${serviceField.name}` as any}
            render={({field}) => (
              <DatePicker
                ref={field.ref}
                dateFormat="dd.MM.yyyy"
                className={
                  (errors.field_values !== undefined 
                    // @ts-ignore
                    && !!errors.field_values[serviceField.name]?.message
                  )
                    ? "is-invalid" 
                    : ""
                }
                selected={field.value}
                id={serviceField.name}
                onChange={field.onChange}
                disabled={props.disabled}
                readOnly={props.readOnly}
                aria-required={props.fld.required}
              />
            )}
          />
        </>
      )
    }
    else if (serviceField.type === "address") {
      return (
        <Controller
          control={control}
          name={`field_values.${serviceField.name}` as any}
          render={({field}) => (
            <AdsAutocomplete
              ref={field.ref}
              value={field.value}
              inputId={serviceField.name}
              onChange={(x) => field.onChange(x ?? "")}
              disabled={props.disabled}
              isReadOnly={props.readOnly}
              isValid={
                !!field.value
              }
              isInvalid={
                // @ts-ignore
                errors.field_values !== undefined && errors.field_values[serviceField.name]
              }
              aria-required={props.fld.required}
            />
          )}
        />
      )
    } else if (serviceField.type === "multi_choice") {
      return (
        <Controller
          control={control}
          name={`field_values.${serviceField.name}` as any}
          render={({field}) => (
            <Select
              ref={field.ref}
              className={`styled-container ${!!props.errorMessage ? 'is-invalid' : ''}`}
              value={mapValueToSelectOptions(field.value, true)}
              inputId={props.fld.name}
              options={allOptions ?? []}
              isMulti
              classNamePrefix="select"
              placeholder={$t(t_key.register_view.select_empty)}
              aria-describedby={
                errors.field_values === undefined 
                  ? "" 
                  // @ts-ignore
                  : errors.field_values[serviceField.name]?.message
              }
              aria-invalid={
                errors.field_values !== undefined 
                  // @ts-ignore
                  && !!errors.field_values[serviceField.name]
              }
              aria-required={props.fld.required}
              onChange={field.onChange}
            />
          )}
        />
      )
    } else if (serviceField.type === "single_choice_dropdown") {
      return (
        <Controller
          control={control}
          name={`field_values.${serviceField.name}` as any}
          render={({field}) => (
            <Select
              ref={field.ref}
              className={`styled-container ${!!props.errorMessage ? 'is-invalid' : ''}`}
              value={mapValueToSelectOptions(field.value, false)}
              inputId={props.fld.name}
              options={allOptions ?? []}
              classNamePrefix="select"
              placeholder={$t(t_key.register_view.select_empty)}
              aria-describedby={
                errors.field_values === undefined
                  ? ""
                  // @ts-ignore
                  : errors.field_values[serviceField.name]?.message
              }
              aria-invalid={
                errors.field_values !== undefined
                  // @ts-ignore
                  && !!errors.field_values[serviceField.name]
              }
              aria-required={props.fld.required}
              onChange={(x) => field.onChange(x ?? null)}
            />
          )}
        />
      )
    } else if (serviceField.type === "multi_choice_checkbox") {
      return (
        <Controller
          control={control}
          name={`field_values.${serviceField.name}` as any}
          render={({field}) => (
            <>
              {(() => {
                const currentValues: string[] = Array.isArray(field.value)
                  ? field.value
                    .map((item: any) => (typeof item === "string" ? item : item?.value))
                    .filter((value: string | undefined): value is string => !!value)
                  : [];
                return (
                  <div 
                    className={`checkbox-group ${!!props.errorMessage ? 'is-invalid' : ''}`}
                    role="group"
                    aria-labelledby={`label-${serviceField.name}`}
                  >
                    {allOptions?.map((option) => (
                      <Form.Check
                        key={option.value}
                        type="checkbox"
                        id={`${serviceField.name}-${option.value}`}
                        label={option.label}
                        value={option.value}
                        checked={currentValues.includes(option.value)}
                        disabled={props.disabled}
                        readOnly={props.readOnly}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const selected = (allOptions ?? []).filter((x) =>
                              [...currentValues, option.value].includes(x.value)
                            );
                            field.onChange(selected);
                          } else {
                            const selected = (allOptions ?? []).filter((x) =>
                              currentValues.filter((value) => value !== option.value).includes(x.value)
                            );
                            field.onChange(selected);
                          }
                        }}
                        aria-invalid={
                          errors.field_values !== undefined 
                            // @ts-ignore
                            && !!errors.field_values[serviceField.name]
                        }
                      />
                    ))}
                  </div>
                );
              })()}
            </>
          )}
        />
      )
    } else if (serviceField.type === "single_choice_checkbox") {
      const fieldLabel = resolveFieldLabel(serviceField);
      return (
        <Controller
          control={control}
          name={`field_values.${serviceField.name}` as any}
          render={({field}) => (
            <Form.Check
              type="checkbox"
              id={serviceField.name}
              label={fieldLabel}
              checked={toBoolean(field.value)}
              disabled={props.disabled}
              readOnly={props.readOnly}
              onChange={(e) => field.onChange(e.target.checked)}
              aria-invalid={
                errors.field_values !== undefined 
                  // @ts-ignore
                  && !!errors.field_values[serviceField.name]
              }
              aria-required={props.fld.required}
            />
          )}
        />
      )
    } else if (serviceField.type === "toggle") {
      const fieldLabel = resolveFieldLabel(serviceField);
      return (
        <Controller
          control={control}
          name={`field_values.${serviceField.name}` as any}
          render={({field}) => (
            <Form.Check
              type="switch"
              id={serviceField.name}
              label={fieldLabel}
              checked={toBoolean(field.value)}
              disabled={props.disabled}
              readOnly={props.readOnly}
              onChange={(e) => field.onChange(e.target.checked)}
              aria-invalid={
                errors.field_values !== undefined 
                  // @ts-ignore
                  && !!errors.field_values[serviceField.name]
              }
              aria-required={props.fld.required}
            />
          )}
        />
      )
    }
    return (
      <Controller 
        control={control}
        name={`field_values.${serviceField.name}` as any}
        render={({field}) => (
          <Form.Control
            id={serviceField.name}
            ref={field.ref}
            readOnly={props.readOnly}
            disabled={props.disabled}
            isValid={
              !!field.value
            }
            isInvalid={
              // @ts-ignore
              errors.field_values !== undefined && errors.field_values[serviceField.name]
            }
            value={field.value}
            onChange={field.onChange}
            aria-describedby={ariaDescription(serviceField.name)}
            aria-invalid={
              errors.field_values !== undefined 
                // @ts-ignore
                && !!errors.field_values[serviceField.name]
            }
            aria-required={props.fld.required}
            required={props.fld.required}
          />
        )}
      />
    )
  }

  const renderFieldDescription = (fieldName: string): JSX.Element => {
    return (
      <>
        {fieldName === RegFieldName.MobilePhone &&
          <i id={AriaDesc.MobilePhone} style={{display: "block"}} tabIndex={-1}>
            {$t(t_key.field.mobile_phone_desc)}
          </i>
        }
      </>
    );

  }

  useEffect(() => {
    if ((props.fld.type !== "multi_choice" &&
         props.fld.type !== "multi_choice_checkbox" &&
         props.fld.type !== "single_choice_dropdown") || !props.fld.choiceOptions) {
      setAllOptions(null);
      return;
    }

    let tmpAll = props.fld.choiceOptions?.map(x => ({
      value: x.value,
      label: resolveChoiceLabel(x.value, x.labels)
    } as SelectOption)) ?? [];

    setAllOptions(tmpAll);
  }, [props.fld.type, props.fld.choiceOptions, i18n.language]);

  const renderMobileFieldLabel = (field: RegField) => {
    const fieldLabel = resolveFieldLabel(field);
    const requiredFieldLabel = `${fieldLabel} ${$t(t_key.validation.required_field)}`

    return (
      <>
        {field.required 
          ?
          <span style={{display: "none"}} id={`required-${field.name}`}>
            {requiredFieldLabel}
          </span>
          : 
          <span style={{display: "none"}} id={`opt-${field.name}`}>
            {fieldLabel}
          </span>
        }
      </>
    )
  }

  const fieldLabel = resolveFieldLabel(props.fld);

  return (
    <Col key={props.fld.name} xs={12} sm={6}>
      {props.isMobile &&
        renderMobileFieldLabel(props.fld)
      }

      <Form.Group className="mb-3" >
        {props.isMobile && props.fld.required && props.fld.type !== "single_choice_checkbox" && props.fld.type !== "toggle" &&
          <Form.Label
            htmlFor={props.fld.name}
            tabIndex={-1}
            aria-labelledby={`required-${props.fld.name}`}
          >
            {fieldLabel} <span className="req-indic" aria-hidden>*</span> 
          </Form.Label>
        }

        {props.isMobile && !props.fld.required && props.fld.type !== "single_choice_checkbox" && props.fld.type !== "toggle" &&
          <Form.Label htmlFor={props.fld.name} tabIndex={-1}>
            {fieldLabel} 
          </Form.Label>
        }

        {!props.isMobile && props.fld.type !== "single_choice_checkbox" && props.fld.type !== "toggle" &&
          <Form.Label htmlFor={props.fld.name} tabIndex={-1}>
            {fieldLabel} 
            { props.fld.required 
              ? <span className="req-indic" aria-hidden={!props.isMobile}>*</span> 
              : null
            }
          </Form.Label>
        }

        {renderFieldControl(props.fld)}
        {renderFieldDescription(props.fld.name)}

        { // @ts-ignore
          errors.field_values && 
            <Form.Text role="alert" className="text-danger" id={`${AriaDesc.ErrorPrefix}${props.fld.name}`}>
              { // @ts-ignore
                errors.field_values[props.fld.name]?.message as string
              }
            </Form.Text>
        }
      </Form.Group>
    </Col>
  )
}

FormField.defaultProps = defaultProps;
